import { useEffect, useId, useRef, useState } from "react";
import SearchIcon from "../../../../assets/icons/search.svg";
import { hasExactEducationMasterMatch } from "../../../../utils/educationHelpers";
import useEducationMasterOptions from "./useEducationMasterOptions";

const EducationMasterField = ({
  label,
  value = "",
  isRequired = false,
  placeholder = "",
  config,
  onChange,
}) => {
  const fieldId = useId();
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const isSearch = config?.mode === "search";
  const trimmedSearch = searchTerm.trim();

  const { options, isLoading, isError, errorMessage, isSearchReady } =
    useEducationMasterOptions({
      categories: config?.categories || [],
      search: isSearch ? searchTerm : "",
      enabled: isOpen,
      minSearchChars: config?.minSearchChars ?? 0,
    });

  const visibleOptions = isSearch
    ? options
    : options.filter((option) =>
        option.toLowerCase().includes(trimmedSearch.toLowerCase()),
      );
  const showAddOption =
    isSearch &&
    config?.allowAdd &&
    isSearchReady &&
    !isLoading &&
    !isError &&
    trimmedSearch.length > 0 &&
    !hasExactEducationMasterMatch(options, trimmedSearch);

  useEffect(() => {
    if (!isOpen) return undefined;
    searchInputRef.current?.focus();
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen]);

  const closeMenu = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const selectOption = (option) => {
    onChange(option);
    closeMenu();
  };

  const openMenu = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setOpenUpward(rect.top > window.innerHeight / 2);
    }
    setIsOpen(true);
  };

  const showResults = isSearch ? isSearchReady : true;

  return (
    <div className="flex flex-col form-detail" ref={containerRef}>
      <div className="emp-form-label" id={fieldId}>
        {label}
        {isRequired ? "*" : ""}
      </div>
      <div className="custom-dropdown-container">
        <div
          className={`custom-dropdown-header ${
            value ? "select-value" : "select-placeholder"
          }`}
          role="combobox"
          aria-expanded={isOpen}
          aria-labelledby={fieldId}
          onClick={() => (isOpen ? closeMenu() : openMenu())}
        >
          <div className="selected-value">
            {value || placeholder || `Select ${String(label || "").toLowerCase()}`}
          </div>
          <div className="dropdown-arrow" />
        </div>
        {isOpen ? (
          <div className={`custom-dropdown-menu ${openUpward ? "opens-up" : ""}`}>
            <div className="dropdown-search-container">
              <img src={SearchIcon} alt="" className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="dropdown-search-input"
                placeholder="Search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onClick={(event) => event.stopPropagation()}
              />
            </div>
            <div className="dropdown-options-list">
              {!showResults ? null : isLoading ? (
                <div className="no-options">Searching...</div>
              ) : isError ? (
                <div className="rr-hr-master-message rr-hr-master-message--error">
                  {errorMessage || "Unable to load suggestions."}
                </div>
              ) : (
                <>
                  {visibleOptions.map((option) => (
                    <div
                      key={option}
                      className={`dropdown-option ${option === value ? "selected" : ""}`}
                      onClick={() => selectOption(option)}
                    >
                      {option}
                    </div>
                  ))}
                  {visibleOptions.length === 0 && !showAddOption ? (
                    <div className="no-options">No matching results found.</div>
                  ) : null}
                  {showAddOption ? (
                    <div
                      className="dropdown-option dropdown-option--add"
                      onClick={() => selectOption(trimmedSearch)}
                    >
                      Add “{trimmedSearch}”
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EducationMasterField;
